import type { WorkItem } from "@gjwtrack/domain";
import { Plus, X } from "lucide-react";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { client, useClient } from "../lib/client.ts";
import { periodText, userName } from "../lib/format.ts";
import { parsePeriod } from "../lib/period.ts";
import { Button, Input } from "../ui/ring.ts";
import { Avatar } from "./Badges.tsx";
import { uuid } from "@gjwtrack/client";

const today = (): string => new Date().toISOString().slice(0, 10);

/** Time tracking: logged work on the issue (YouTrack's "Spent time" panel) and a form to add more. */
export function WorkItems({ issueId, updatedAt, canLog }: { issueId: number; updatedAt: number; canLog: boolean }) {
  const [items, setItems] = useState<WorkItem[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ date: today(), duration: "", type: "", text: "" });
  const [error, setError] = useState<string | null>(null);
  const schema = useClient((s) => s.schema);
  const me = useClient((s) => s.me);
  const reload = useCallback(() => client.http.workItems(issueId).then(setItems).catch(() => setItems([])), [issueId]);
  useEffect(() => {
    void reload();
  }, [reload, updatedAt]);
  const total = (items ?? []).reduce((s, w) => s + w.minutes, 0);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const minutes = parsePeriod(form.duration);
    if (!minutes) {
      setError("Duration like 1h 30m, 45m or 1d (8h).");
      return;
    }
    setError(null);
    try {
      await client.http.command("addWorkItem", { issueId, date: Date.parse(`${form.date}T00:00:00Z`), minutes, type: form.type.trim() || null, text: form.text.trim() || null }, uuid());
      setForm({ date: today(), duration: "", type: "", text: "" });
      setAdding(false);
      await reload();
    } catch (err) {
      setError((err as Error).message);
    }
  };
  const remove = async (w: WorkItem): Promise<void> => {
    if (!confirm(`Delete ${periodText(w.minutes)} logged on ${new Date(w.date).toISOString().slice(0, 10)}?`)) return;
    await client.http.command("deleteWorkItem", { workItemId: w.id }, uuid()).catch((err) => client.pushError("work_item", (err as Error).message));
    await reload();
  };

  if (items === null) return null;
  if (items.length === 0 && !canLog) return null;
  return (
    <section className="mb-8 ml-10">
      <div className="mb-1 flex items-center gap-2 text-[13px] text-muted">
        <span>Spent time</span>
        {total > 0 && <b className="text-ink">{periodText(total)}</b>}
        <span className="flex-1" />
        {canLog && !adding && (
          <Button inline icon={Plus} onClick={() => setAdding(true)}>
            Add spent time
          </Button>
        )}
      </div>
      {items.length > 0 && (
        <table className="w-full text-[13px]">
          <tbody>
            {items.map((w) => (
              <tr key={w.id} className="group">
                <td className="w-28 py-1 text-muted tabular-nums">{new Date(w.date).toISOString().slice(0, 10)}</td>
                <td className="w-40 py-1">
                  <span className="flex items-center gap-1.5">
                    {w.authorId !== null && <Avatar schema={schema} userId={w.authorId} size={18} />}
                    <span className="truncate">{w.authorId === null ? "—" : userName(schema, w.authorId)}</span>
                  </span>
                </td>
                <td className="w-20 py-1 text-right font-medium tabular-nums">{periodText(w.minutes)}</td>
                <td className="w-32 py-1 text-muted">{w.type ?? ""}</td>
                <td className="py-1">{w.text ?? ""}</td>
                <td className="w-6 py-1">
                  {(canLog || w.authorId === me?.id) && (
                    <button className="icon-btn h-5 w-5 opacity-0 group-hover:opacity-100" title="Delete" onClick={() => void remove(w)}>
                      <X size={12} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {adding && (
        <form className="mt-2 flex flex-wrap items-end gap-2" onSubmit={(e) => void submit(e)}>
          <input type="date" className="field" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
          <Input placeholder="1h 30m" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} autoFocus className="w-24" />
          <Input placeholder="Type (Development…)" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-40" />
          <Input placeholder="What was done" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} className="min-w-48 flex-1" />
          <Button primary type="submit" disabled={!form.duration.trim()}>
            Add
          </Button>
          <Button type="button" onClick={() => setAdding(false)}>
            Cancel
          </Button>
          {error && <span className="text-danger">{error}</span>}
        </form>
      )}
    </section>
  );
}
